import { ArrowRight, FileSpreadsheet, RefreshCw, Upload } from 'lucide-react'

import { Link } from '@/lib/router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Kpi } from '@/components/ui/kpi'
import { Skeleton } from '@/components/ui/skeleton'

import { ageingBucket, bucketColor, bucketLabel, date, dpd, inr, num, priorityColor, relativeTime } from '@/lib/format'
import { useMe } from '@/features/auth/api'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import { FreshnessBadge } from '@/features/freshness/FreshnessBadge'
import {
  useAgentProductivity,
  usePtpStats,
  useReconAging,
  useUnallocated,
  type AgentProductivityRow,
  type ReconAging as ReconAgingShape,
  type Unallocated,
} from '@/features/cases/api'

import { useDashboardSummary, type SummaryCell } from './api'
import { CollectionSplitStrip } from './CollectionSplitStrip'
import { FieldTripsCard } from './FieldTripsCard'
import { LmsSyncCard } from './LmsSyncCard'

export function DashboardPage() {
  const { data: me } = useMe()
  const s = useDashboardSummary()
  const { data: ptp } = usePtpStats()
  const { data: agents } = useAgentProductivity(7, 8)
  const { data: reconAging } = useReconAging()
  const { data: unallocated } = useUnallocated()

  const greetingName = me?.empId ?? 'team'
  // Cosmetic — /ingest enforces INGESTION_UPLOAD itself — but a link the
  // server then refuses is worse than no link.
  const canUpload = navPermissionCheck(me)('INGESTION_UPLOAD')

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Today</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy-900">
            Good to see you, {greetingName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Portfolio snapshot{' '}
            {s.asOfDate ? (
              <>as of <span className="font-medium text-navy-800">{date(s.asOfDate)}</span></>
            ) : (
              'as of latest DPD upload'
            )}
            .
          </p>
          <FreshnessBadge className="mt-2" />
        </div>
        <div className="flex items-center gap-2">
          {canUpload && (
            <Link to="/ingest">
              <Button variant="outline" size="sm">
                <Upload className="h-3.5 w-3.5" />
                Upload file
              </Button>
            </Link>
          )}
          <Link to="/cases">
            <Button size="sm">
              Open the queue
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Graviton tier: dues come from the LMS, not uploads. */}
      {me?.tenantTier === 'GRAVITON' && <LmsSyncCard canRefresh={canUpload} />}

      {/* KPI row — each tile is drillable; the hover lift is the affordance. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <DrillKpi to="/cases" search={{}}>
          <Kpi
            label="Open cases"
            value={num(s.openTotal)}
            hint={s.hasAnyCases ? `${num(s.p1Count)} priority P1 · click for queue` : 'No cases yet — upload DPD dues'}
            loading={s.loading}
            deltaSense="lower-is-better"
          />
        </DrillKpi>
        <DrillKpi to="/cases" search={{}}>
          <Kpi
            label="Outstanding (open)"
            value={inr(s.totalOutstanding)}
            hint={s.hasAnyCases ? `Across ${num(s.openTotal)} loans · click for queue` : '—'}
            loading={s.loading}
            deltaSense="lower-is-better"
          />
        </DrillKpi>
        <DrillKpi to="/cases" search={{}}>
          <Kpi
            label="P1 priority"
            value={num(s.p1Count)}
            hint={s.p2Count > 0 ? `+ ${num(s.p2Count)} P2 · click to drill` : 'High-urgency work first'}
            loading={s.loading}
            deltaSense="lower-is-better"
          />
        </DrillKpi>
      </div>

      {/* Paid vs unpaid — the agent app's "My cases" split, across the manager's book */}
      <CollectionSplitStrip split={s.collection} loading={s.loading} />

      {/* PTP lifecycle strip — keeps the "time is participating" story honest */}
      <PtpStrip ptp={ptp} />

      {/* Recon aging + unallocated — operational health one row below PTP */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ReconAgingCard data={reconAging} />
        <UnallocatedCard data={unallocated} />
      </div>

      {/* Aging × outstanding heatmap — bucket × priority grid */}
      <AgingHeatmap cells={s.cells} openTotal={s.openTotal} />

      {/* Agent productivity table */}
      <AgentProductivityCard rows={agents} />

      {/* Field-trip snapshot — live count + recently flagged trips. */}
      <FieldTripsCard />

      {/* Bucket distribution + recent batches */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>Bucket distribution</CardTitle>
                <CardDescription>Open cases by current DPD bucket.</CardDescription>
              </div>
              {!s.loading && (
                <Badge tone="navy">Tabular nums · INR compact</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {s.loading ? (
              <div className="space-y-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-7 w-full" />
                ))}
              </div>
            ) : !s.hasAnyCases ? (
              <EmptyHint
                title="No bucket data yet"
                body="Upload a DPD dues file to populate the bucket distribution. Reconciliation will then open cases for delinquent loans."
                cta={
                  canUpload && (
                    <Link to="/ingest">
                      <Button size="sm" variant="secondary">
                        <Upload className="h-3.5 w-3.5" />
                        Upload DPD dues
                      </Button>
                    </Link>
                  )
                }
              />
            ) : (
              <BucketBars buckets={s.buckets} maxCount={Math.max(1, ...s.buckets.map((b) => b.count))} />
            )}
          </CardContent>
          <CardFooter>
            <span className="text-2xs uppercase tracking-wider text-muted-foreground">
              Red is reserved for negative numeric values
            </span>
            <Link to="/cases" className="inline-flex items-center gap-1 text-xs font-medium text-navy-700 hover:underline">
              All cases <ArrowRight className="h-3 w-3" />
            </Link>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>Recent uploads</CardTitle>
                <CardDescription>Latest 5 ingestion batches.</CardDescription>
              </div>
              <RefreshCw className="h-3.5 w-3.5 text-navy-500" />
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {s.loading ? (
              <div className="space-y-2 px-5 pb-4">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
              </div>
            ) : !s.hasAnyBatches ? (
              <div className="px-5 pb-4">
                <EmptyHint
                  title="No uploads yet"
                  body="Drop the first Customer / Loan / DPD file in Ingest."
                />
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {s.recentBatches!.map((b) => (
                  <li key={b.batchId}>
                    <Link
                      to="/ingest/$batchId"
                      params={{ batchId: b.batchId }}
                      className="flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-fluid ease-fluid hover:bg-navy-50"
                    >
                      <FileSpreadsheet className="h-3.5 w-3.5 shrink-0 text-navy-500" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-navy-900">{b.fileName}</span>
                        <span className="block text-2xs uppercase tracking-wider text-muted-foreground">
                          {b.kind.replace(/_/g, ' ')} · {relativeTime(b.submittedAt)}
                        </span>
                      </span>
                      <StatusBadge status={b.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top priorities */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Top priorities right now</CardTitle>
              <CardDescription>Highest-scoring open cases. Click to open a case.</CardDescription>
            </div>
            <Link to="/cases" className="text-2xs font-medium uppercase tracking-wider text-navy-700 hover:underline">
              View all
            </Link>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {s.loading ? (
            <div className="space-y-2 px-5 pb-4">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !s.hasAnyCases ? (
            <div className="px-5 pb-4">
              <EmptyHint
                title="The queue is empty"
                body="Upload a DPD dues file with delinquent loans. The reconciliation engine will open cases prioritised by bucket and outstanding."
                cta={
                  canUpload && (
                    <Link to="/ingest">
                      <Button size="sm">
                        <Upload className="h-3.5 w-3.5" />
                        Go to Ingest
                      </Button>
                    </Link>
                  )
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-5 py-2 font-medium">Priority</th>
                    <th className="px-3 py-2 font-medium">Loan</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Product</th>
                    <th className="px-3 py-2 text-right font-medium">DPD</th>
                    <th className="px-3 py-2 font-medium">Bucket</th>
                    <th className="px-3 py-2 text-right font-medium">Outstanding</th>
                    <th className="px-5 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {s.topCases!.map((c) => {
                    const pc = priorityColor(c.priorityBand)
                    return (
                      <tr key={c.caseId} className="border-b border-slate-200 last:border-0 transition-colors duration-fluid ease-fluid hover:bg-navy-50">
                        <td className="px-5 py-2">
                          <span
                            className="inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider"
                            style={{ background: pc.bg, color: pc.fg }}
                          >
                            {c.priorityBand ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-navy-900">{c.loanAccountNo}</td>
                        <td className="px-3 py-2 text-navy-900">{c.customerName}</td>
                        <td className="px-3 py-2 text-muted-foreground">{c.productType}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-navy-900">{dpd(c.currentDpd)}</td>
                        <td className="px-3 py-2">
                          <Badge tone={bucketTone(c.currentBucket)}>{bucketLabel(c.currentBucket)}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-navy-900">{inr(c.currentOutstanding)}</td>
                        <td className="px-5 py-2 text-right">
                          <Link
                            to="/cases"
                            search={{ case: c.caseId } as never}
                            className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider text-navy-700 hover:underline"
                          >
                            Open <ArrowRight className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/** Stacked horizontal bars per bucket — each row drills into /cases?bucket=. */
function BucketBars({ buckets, maxCount }: { buckets: ReturnType<typeof useDashboardSummary>['buckets']; maxCount: number }) {
  return (
    <div className="space-y-2.5">
      {buckets.map((b) => {
        const pct = Math.max(2, Math.round((b.count / maxCount) * 100))
        return (
          <Link
            key={b.bucket}
            to="/cases"
            search={{ bucket: b.bucket } as never}
            className="grid grid-cols-[5.5rem_1fr_8rem] items-center gap-3 rounded-md p-1 -m-1 transition-colors hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
            title={`Open cases in bucket ${b.label}`}
          >
            <span className="text-xs font-medium uppercase tracking-wider text-navy-700">
              Bucket {b.label}
            </span>
            <div className="relative h-6 overflow-hidden rounded bg-slate-100">
              <div
                className="h-full rounded transition-all duration-fluid ease-fluid"
                style={{ width: `${pct}%`, background: bucketColor(b.bucket) }}
              />
              <span className="absolute inset-0 flex items-center px-2 text-2xs font-semibold tabular-nums text-navy-900">
                {num(b.count)}
              </span>
            </div>
            <span className="text-right text-xs tabular-nums text-navy-900">
              {inr(b.outstanding)}
            </span>
          </Link>
        )
      })}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone = (() => {
    if (status === 'DONE') return 'positive' as const
    if (status === 'PARTIALLY_DONE') return 'warning' as const
    if (status === 'FAILED') return 'negative' as const
    if (status === 'RUNNING' || status === 'QUEUED') return 'info' as const
    return 'slate' as const
  })()
  return <Badge tone={tone}>{status.replace('_', ' ')}</Badge>
}

/**
 * PTP lifecycle strip — open, due today, due soon, overdue, plus a 30-day
 * keep-rate. The numbers are honest because the expiry sweep keeps OPEN
 * from rotting and the retroactive resolver flips late-paid promises back
 * to KEPT. Two days ago this card would have been a lie.
 */
/**
 * Wraps a Kpi as a clickable router Link. Preserves the existing
 * card affordance (hover:shadow-lift) and adds focus styling so keyboard
 * users get a clear ring. Use this for any tile whose number maps cleanly
 * to a filtered case queue or another deep page.
 */
function DrillKpi({
  to,
  search,
  children,
}: {
  to: string
  search: Record<string, unknown>
  children: React.ReactNode
}) {
  return (
    <Link
      to={to}
      // TanStack's `search` prop is strict-typed per registered route;
      // we keep this loose because the routes we touch (/cases, /reconciliation)
      // accept arbitrary search params.
      search={search as never}
      className="block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
    >
      {children}
    </Link>
  )
}

function PtpStrip({ ptp }: { ptp: ReturnType<typeof usePtpStats>['data'] }) {
  const loading = !ptp

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <DrillKpi to="/cases" search={{ ptp: 'OPEN' }}>
        <Kpi
          label="PTPs open"
          value={ptp ? num(ptp.open) : '—'}
          hint="Click to filter the queue"
          loading={loading}
          deltaSense="neutral"
        />
      </DrillKpi>
      <DrillKpi to="/cases" search={{ ptp: 'DUE_3D' }}>
        <Kpi
          label="Due in 3d"
          value={ptp ? num(ptp.dueIn3d) : '—'}
          hint="Includes today"
          loading={loading}
          deltaSense="neutral"
        />
      </DrillKpi>
    </div>
  )
}

/**
 * Open reconciliation findings grouped by detected age. The big number is
 * open-total; the breakdown bars show how stale the queue is. If the bulk
 * sit in the `>7d` slot, ops is behind.
 */
function ReconAgingCard({ data }: { data: ReconAgingShape | undefined }) {
  const total = data?.openTotal ?? 0
  const buckets = data
    ? [
        { label: 'Today',    count: data.openToday,   tone: 'positive' as const },
        { label: '1–3 days', count: data.open1to3d,   tone: 'navy' as const },
        { label: '4–7 days', count: data.open4to7d,   tone: 'warning' as const },
        { label: '> 7 days', count: data.openOver7d,  tone: 'negative' as const },
      ]
    : []
  const max = Math.max(1, ...buckets.map((b) => b.count))

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Reconciliation aging</CardTitle>
            <CardDescription>
              Open findings by how long they've waited for ops.
            </CardDescription>
          </div>
          <Link
            to="/reconciliation"
            className="block rounded-md p-1 -m-1 text-right transition-colors hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
            title="Open the reconciliation review"
          >
            <div className="text-2xl font-semibold tabular-nums text-navy-900">{num(total)}</div>
            <div className="text-2xs uppercase tracking-wider text-muted-foreground">open ›</div>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5 pb-4">
        {!data && <Skeleton className="h-20 w-full" />}
        {data && total === 0 && (
          <p className="py-2 text-sm text-muted-foreground">
            No open findings. Either nothing's drifting, or the recon engine hasn't
            run against today's batch yet.
          </p>
        )}
        {data && total > 0 && buckets.map((b) => {
          const pct = Math.max(2, Math.round((b.count / max) * 100))
          return (
            <Link
              key={b.label}
              to="/reconciliation"
              className="grid grid-cols-[5rem_1fr_3rem] items-center gap-3 rounded-md p-1 -m-1 transition-colors hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
              title={`Open ${b.label.toLowerCase()} findings`}
            >
              <span className="text-xs font-medium text-navy-700">{b.label}</span>
              <div className="relative h-5 overflow-hidden rounded bg-slate-100">
                <div
                  className="h-full rounded transition-all duration-fluid ease-fluid"
                  style={{
                    width: `${pct}%`,
                    background:
                      b.tone === 'positive' ? 'var(--positive)' :
                      b.tone === 'negative' ? 'var(--negative)' :
                      b.tone === 'warning'  ? 'var(--warning)' :
                                              '#204887',
                  }}
                />
              </div>
              <span className="text-right text-xs tabular-nums text-navy-900">{num(b.count)}</span>
            </Link>
          )
        })}
        {data && data.byKind.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 pt-2">
            {data.byKind.map((k) => (
              <Link
                key={k.kind}
                to="/reconciliation"
                search={{ kind: k.kind } as never}
                className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
                title={`Filter to ${k.kind.replace(/_/g, ' ').toLowerCase()}`}
              >
                <Badge tone="slate">
                  {k.kind.replace(/_/g, ' ').toLowerCase()} · {num(k.count)}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Link
          to="/reconciliation"
          className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider text-navy-700 hover:underline"
        >
          Open findings <ArrowRight className="h-3 w-3" />
        </Link>
        {data?.oldestOpen && (
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            Oldest: {relativeTime(data.oldestOpen)}
          </span>
        )}
      </CardFooter>
    </Card>
  )
}

/**
 * Open cases without an ACTIVE allocation, broken down by bucket. This is the
 * "what does ops need to allocate today" tile.
 */
function UnallocatedCard({ data }: { data: Unallocated | undefined }) {
  const total = data?.total ?? 0
  const max = Math.max(1, ...(data?.byBucket ?? []).map((b) => b.count))

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Unallocated cases</CardTitle>
            <CardDescription>
              Open cases without an ACTIVE FIELD/TELE allocation.
            </CardDescription>
          </div>
          <Link
            to="/allocations"
            className="block rounded-md p-1 -m-1 text-right transition-colors hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
            title="Open allocations"
          >
            <div className="text-2xl font-semibold tabular-nums text-navy-900">{num(total)}</div>
            <div className="text-2xs uppercase tracking-wider text-muted-foreground">to assign ›</div>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5 pb-4">
        {!data && <Skeleton className="h-20 w-full" />}
        {data && total === 0 && (
          <p className="py-2 text-sm text-muted-foreground">
            Every open case has at least one ACTIVE allocation. Nothing to assign.
          </p>
        )}
        {data && data.byBucket.map((b) => {
          const pct = Math.max(2, Math.round((b.count / max) * 100))
          return (
            <Link
              key={b.bucket}
              to="/cases"
              search={{ bucket: b.bucket } as never}
              className="grid grid-cols-[5.5rem_1fr_8rem] items-center gap-3 rounded-md p-1 -m-1 transition-colors hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
              title={`Open cases in bucket ${bucketLabel(b.bucket)}`}
            >
              <span className="text-xs font-medium uppercase tracking-wider text-navy-700">
                {bucketLabel(b.bucket)}
              </span>
              <div className="relative h-5 overflow-hidden rounded bg-slate-100">
                <div
                  className="h-full rounded transition-all duration-fluid ease-fluid"
                  style={{ width: `${pct}%`, background: bucketColor(b.bucket) }}
                />
                <span className="absolute inset-0 flex items-center px-2 text-2xs font-semibold tabular-nums text-navy-900">
                  {num(b.count)}
                </span>
              </div>
              <span className="text-right text-xs tabular-nums text-navy-900">{inr(b.outstanding)}</span>
            </Link>
          )
        })}
      </CardContent>
      <CardFooter>
        <Link
          to="/allocations"
          className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider text-navy-700 hover:underline"
        >
          Allocate now <ArrowRight className="h-3 w-3" />
        </Link>
      </CardFooter>
    </Card>
  )
}

/**
 * Top agents by dispositions over the trailing 7d. Shows connect rate, PTP
 * rate, and 30d keep rate side-by-side. Tabular numerals throughout.
 */
function AgentProductivityCard({ rows }: { rows: AgentProductivityRow[] | undefined }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Agent productivity · last 7 days</CardTitle>
            <CardDescription>
              Ranked by total dispositions. Connect-rate = connects ÷ all
              dispositions. PTP-rate = promise outcomes ÷ connects.
              Keep-rate = KEPT ÷ resolved PTPs (30d).
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {!rows && (
          <div className="space-y-2 px-5 pb-4">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        )}
        {rows && rows.length === 0 && (
          <div className="px-5 py-6 text-center">
            <p className="text-sm font-medium text-navy-900">No dispositions yet this week.</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
              The strip lights up as soon as agents start capturing in the queue.
            </p>
          </div>
        )}
        {rows && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                <tr>
                  <th className="px-5 py-2 font-medium">Agent</th>
                  <th className="px-3 py-2 text-right font-medium">Dispos</th>
                  <th className="px-3 py-2 text-right font-medium">Connects</th>
                  <th className="px-3 py-2 text-right font-medium">Connect %</th>
                  <th className="px-3 py-2 text-right font-medium">Promises</th>
                  <th className="px-3 py-2 text-right font-medium">PTP %</th>
                  <th className="px-3 py-2 text-right font-medium">PTPs kept (30d)</th>
                  <th className="px-5 py-2 text-right font-medium">Keep-rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.userId} className="border-b border-slate-200 last:border-0">
                    <td className="px-5 py-2">
                      <div className="text-navy-900">{r.fullName ?? r.empId}</div>
                      <div className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">{r.empId}</div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-navy-900">{num(r.dispositions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy-900">{num(r.connects)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy-800">{pct(r.connectRate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy-900">{num(r.promises)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy-800">{pct(r.ptpRate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy-900">{num(r.ptpsKept30d)}</td>
                    <td className="px-5 py-2 text-right tabular-nums font-medium text-navy-900">{pct(r.keepRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Aging × outstanding heatmap — buckets across, priority bands down,
 * each cell coloured by its share of the open-case count and stamped
 * with the case count + INR sum. Click any cell to drill into the
 * filtered queue. Pure SVG so it scales to any size without canvas
 * resize bugs and prints crisply.
 */
function AgingHeatmap({ cells, openTotal }: { cells: SummaryCell[]; openTotal: number }) {
  const buckets = [
    { key: 'CURRENT',        label: '0' },
    { key: 'BUCKET_1_30',    label: '1–30' },
    { key: 'BUCKET_31_60',   label: '31–60' },
    { key: 'BUCKET_61_90',   label: '61–90' },
    { key: 'BUCKET_90_PLUS', label: '90+' },
  ]
  // NPA and WRITE_OFF roll into the 90+ column (ageingBucket). Before this
  // they matched no column and were silently dropped (631 NPA cases missing
  // from a 2,888 total).
  const bands = ['P1', 'P2', 'P3', 'P4']

  // The server already grouped by (bucket × band) over every open case; this
  // only lays those cells out. Counting raw rows here would re-introduce the
  // page cap the summary endpoint exists to remove.
  const grid: Record<string, Record<string, { count: number; out: number }>> = {}
  for (const band of bands) {
    grid[band] = {}
    for (const b of buckets) grid[band][b.key] = { count: 0, out: 0 }
  }
  let maxCount = 0
  for (const c of cells) {
    const band = c.priorityBand ?? 'P4'
    const bucket = ageingBucket(c.bucket ?? 'BUCKET_1_30')
    if (!grid[band] || !grid[band][bucket]) continue
    const cell = grid[band][bucket]
    cell.count += c.count
    const o = Number(c.outstanding ?? 0)
    if (Number.isFinite(o)) cell.out += o
    if (cell.count > maxCount) maxCount = cell.count
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Aging × priority heatmap</CardTitle>
            <CardDescription>
              Open case count + outstanding by (bucket × priority). Click a cell
              to drill in.
            </CardDescription>
          </div>
          <Badge tone="navy">{num(openTotal)} open</Badge>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {openTotal === 0 ? (
          <p className="px-5 pb-4 text-sm text-muted-foreground">
            No open cases yet — upload a DPD batch.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="px-5 py-2 text-left text-2xs uppercase tracking-wider text-navy-700">
                    Priority
                  </th>
                  {buckets.map((b) => (
                    <th
                      key={b.key}
                      className="px-3 py-2 text-center text-2xs uppercase tracking-wider text-navy-700"
                      style={{ color: bucketColor(b.key) }}
                    >
                      Bucket {b.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bands.map((band) => (
                  <tr key={band}>
                    <td className="px-5 py-1.5">
                      <span
                        className="inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider"
                        style={{
                          background: priorityColor(band).bg,
                          color: priorityColor(band).fg,
                        }}
                      >
                        {band}
                      </span>
                    </td>
                    {buckets.map((b) => {
                      const cell = grid[band]?.[b.key] ?? { count: 0, out: 0 }
                      return (
                        <HeatCell
                          key={b.key}
                          band={band}
                          bucket={b.key}
                          count={cell.count}
                          outstanding={cell.out}
                          maxCount={Math.max(1, maxCount)}
                        />
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <span className="text-2xs uppercase tracking-wider text-muted-foreground">
          Cell heat = share of open cases · INR compact
        </span>
        <Link
          to="/cases"
          className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider text-navy-700 hover:underline"
        >
          Open queue <ArrowRight className="h-3 w-3" />
        </Link>
      </CardFooter>
    </Card>
  )
}

function HeatCell({
  band,
  bucket,
  count,
  outstanding,
  maxCount,
}: {
  band: string
  bucket: string
  count: number
  outstanding: number
  maxCount: number
}) {
  // Map share-of-max to alpha; floor it so cells with even 1 case still
  // tint the surface. Use the bucket's own colour so the heatmap reads
  // as "bucket urgency × priority urgency" without needing a legend.
  const share = count === 0 ? 0 : Math.max(0.08, Math.min(0.7, count / maxCount))
  const base = bucketColor(bucket)
  return (
    <td className="px-1 py-1">
      <Link
        to="/cases"
        search={{ bucket } as never}
        title={`P${band[1]} · ${bucket} · ${num(count)} cases · ${inr(outstanding)}`}
        className="block h-16 rounded-md border border-slate-200 p-1.5 text-center transition-shadow hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
        style={{
          background:
            count === 0
              ? 'rgba(255,255,255,1)'
              : `color-mix(in srgb, ${base} ${share * 100}%, white)`,
        }}
      >
        <div
          className="text-base font-semibold tabular-nums leading-tight"
          style={{
            color: count === 0 ? 'var(--muted-foreground)' : '#0E2138',
          }}
        >
          {count === 0 ? '·' : num(count)}
        </div>
        {count > 0 && (
          <div
            className="text-2xs font-medium tabular-nums leading-tight"
            style={{ color: '#0E2138', opacity: 0.7 }}
          >
            {inr(outstanding)}
          </div>
        )}
      </Link>
    </td>
  )
}

function pct(v: string | null): string {
  if (v == null) return '—'
  const n = Number(v)
  if (!isFinite(n)) return '—'
  return (n * 100).toFixed(0) + '%'
}

function bucketTone(b: string | null | undefined): 'positive' | 'navy' | 'warning' | 'negative' | 'slate' {
  switch (b) {
    case 'CURRENT':         return 'positive'
    case 'BUCKET_1_30':     return 'navy'
    case 'BUCKET_31_60':    return 'warning'
    case 'BUCKET_61_90':    return 'warning'
    case 'BUCKET_90_PLUS':  return 'negative'
    default:                return 'slate'
  }
}

function EmptyHint({ title, body, cta }: { title: string; body: string; cta?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-slate-200 bg-navy-50/40 p-5 text-center">
      <p className="text-sm font-medium text-navy-900">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">{body}</p>
      {cta && <div className="mt-3 flex justify-center">{cta}</div>}
    </div>
  )
}
